// Catalog.ID Client — Claim Detail Pane (view / edit / new)
// ============================================================================
// Single pane type handling: viewing a claim, editing it, and creating new claims.
// Mode is determined by context: { claimUuid } = view/edit, { mode: 'new' } = create.

import { createEntityPaneType, createElement, createFieldGroup, createFormRow, createCopyButton } from '@catalog-shell';
import type { EntityPaneConfig, EntityAction, ViewFieldDef, FormFieldDef, SelectOption, ShellAPI } from '@catalog-shell';
import { createCertificateBadge, deriveValidationStatus } from '../components/certificate-badge';
import { createPublicationBadge } from '../components/publication-badge';
import { derivePublicationStatus } from '../components/publication-status';
import { claimTypeLabel } from './claims-list-pane';
import * as claimsApi from '../api/claims';
import * as publicClaimsApi from '../api/public-claims';
import * as organizationApi from '../api/organization';
import * as accountApi from '../api/account';
import * as trustApi from '../api/trust';
import { decryptClaim, decryptLabel, encryptClaim, reEncryptClaimKey, reEncryptClaimKeyWithChain, reEncryptClaimValue, unwrapSymmetricKey, unwrapSymmetricKeyChain } from '../crypto/claims';
import * as aes from '../crypto/aes';
import { keys } from '../state/keys';
import { getOrgAttrKey, getOrgAttrKeyChain } from '../state/org-keys';
import { identity } from '../state/identity';
import { session } from '../state/session';
import type { ClaimDetail, PublicClaimSummary } from '../api/types';
import { PANE_IDS } from '../shell-config';

// ============================================================================
// View Model
// ============================================================================

interface ClaimViewModel {
    claimUuid: string;
    claimType: string;
    decryptedValue: string;
    label: string;
    validationStatus: string;
    revokedAt: string | null;
    creationTime: string;
    certificates: ClaimDetail['certificates'];
    claimKeys: ClaimDetail['keys'];
    sharingCount: number;
    vouchingStatus: string;
    isOrgClaim: boolean;
    /** The claim's publicclaim row (operator-review lifecycle), if ever published. */
    publication: PublicClaimSummary | null;
}

function emptyModel(isOrgClaim = false): ClaimViewModel {
    return {
        claimUuid: '',
        claimType: '',
        decryptedValue: '',
        label: '',
        validationStatus: 'unvalidated',
        revokedAt: null,
        creationTime: '',
        certificates: [],
        claimKeys: [],
        sharingCount: 0,
        vouchingStatus: '',
        isOrgClaim,
        publication: null,
    };
}

/**
 * The blank model for the NEW form, seeded from the pane context.
 *
 * `isOrgClaim` has to survive into the model: it is the only thing that tells
 * `onCreate` whose key to encrypt to, and by then the context is no longer the
 * only reader. The org-claims pane sets it when it opens this pane
 * (`{ mode: 'new', isOrgClaim: true }`).
 */
export function newClaimModel(context: Record<string, unknown>): ClaimViewModel {
    return emptyModel(context['isOrgClaim'] === true);
}

/** Where a newly created claim goes, and whose key its symmetric key is wrapped to. */
export interface ClaimCreateTarget {
    /** 'own' = the session account's attr_encrypt key; 'org' = the organization's. */
    keySource: 'own' | 'org';
    /** The organization the claim is created under, or null for a personal claim. */
    orgCid: string | null;
    /** Whether a fresh `email` claim may be submitted for validation right away. */
    autoValidateEmail: boolean;
}

/**
 * Decide the create target for a claim.
 *
 * A delegate ALWAYS has an organization in context, so the org branch is taken
 * on the pane's `isOrgClaim` flag alone — never on "is there an org?". An org
 * claim is wrapped to the ORGANIZATION's attr_encrypt public key and POSTed to
 * the org's claims route, so the organization account owns it; wrapping it to
 * the delegate's own key would create a claim the organization can never read.
 *
 * Auto-validation is personal-only: it re-encrypts the claim key with the
 * creator's own attr_encrypt SECRET key, which cannot open a blob wrapped to
 * the org's public key. Org-claim validation needs the unwrapped org key and is
 * out of scope here.
 */
export function claimCreateTarget(isOrgClaim: boolean, orgCid: string | null): ClaimCreateTarget {
    if (!isOrgClaim) return { keySource: 'own', orgCid: null, autoValidateEmail: true };
    if (!orgCid) throw new Error('No organization in context for this org claim');
    return { keySource: 'org', orgCid, autoValidateEmail: false };
}

async function getDecryptionKey(isOrgClaim: boolean): Promise<string | null> {
    // Personal claim: the session's own key. Org claim: the org's ORIGINAL
    // (keyIndex 0) key, unwrapped from the delegate's copy — the single-layer
    // path. Post-rotation claims take the chain-walking path via
    // getOrgAttrKeyChain() instead.
    return isOrgClaim ? await getOrgAttrKey() : keys.getPrivateKey('attr_encrypt');
}

async function loadModel(claimUuid: string, isOrgClaim = false): Promise<ClaimViewModel> {
    const detail = await claimsApi.getClaim(claimUuid);

    // Publication state is supplementary — a lookup failure must not block the
    // claim view. listPublicClaims is scoped to the session account, so org
    // claims (owned by the org account) resolve to null here; the publication
    // field is hidden for them rather than shown as a false "Not published".
    let publication: PublicClaimSummary | null = null;
    if (!isOrgClaim) {
        try {
            const pubs = await publicClaimsApi.listPublicClaims();
            publication = pubs.find(p => p.claimUuid === claimUuid) ?? null;
        } catch { /* leave null */ }
    }

    let decryptedValue = '[encrypted — keys not available]';
    let label = '';
    const attrKey = await getDecryptionKey(isOrgClaim);
    if (attrKey) {
        const selfKey = detail.keys.find(k => k.recipientType === 'self');
        if (selfKey) {
            try {
                let symKey: Uint8Array;
                if (selfKey.keyIndex > 0 && !isOrgClaim) {
                    // Multi-layer rotation: walk the key chain to unwrap
                    const keyChain = keys.getKeyChain('attr_encrypt');
                    symKey = await unwrapSymmetricKeyChain(selfKey.encryptedKey, keyChain, selfKey.keyIndex);
                } else if (selfKey.keyIndex > 0 && isOrgClaim) {
                    // Org claim after key rotation: walk the org key chain
                    const orgChain = await getOrgAttrKeyChain();
                    symKey = await unwrapSymmetricKeyChain(selfKey.encryptedKey, orgChain, selfKey.keyIndex);
                } else {
                    // Standard single-layer unwrap
                    symKey = await unwrapSymmetricKey(aes.fromBase64(selfKey.encryptedKey), aes.fromBase64(attrKey));
                }
                const ct = aes.fromBase64(detail.encryptedValue);
                const n = aes.fromBase64(detail.nonce);
                const plaintext = await aes.decrypt(symKey, n, ct);
                decryptedValue = aes.decodeUtf8(plaintext);
                // Decrypt label if present (encrypted with the same claim symmetric key)
                if (detail.encryptedLabel && detail.labelNonce) {
                    label = await decryptLabel(detail.encryptedLabel, detail.labelNonce, symKey);
                }
            } catch { decryptedValue = '[decryption failed]'; }
        }
    }

    return {
        claimUuid: detail.claimUuid,
        claimType: detail.claimType,
        decryptedValue,
        label,
        validationStatus: detail.validationStatus,
        revokedAt: detail.revokedAt,
        creationTime: detail.creationTime,
        certificates: detail.certificates,
        claimKeys: detail.keys,
        sharingCount: detail.keys.filter(k => k.recipientType !== 'self').length,
        vouchingStatus: '',
        isOrgClaim,
        publication,
    };
}

// ============================================================================
// Claim type options per identity type
// ============================================================================

const CLAIM_TYPES_BY_IDENTITY: Record<string, string[]> = {
    natural_person: [
        'email', 'phone', 'postal_address', 'bank_account', 'date_of_birth',
        'display_name', 'professional_role', 'organization', 'website', 'location', 'biography',
    ],
    organization: [
        'display_name', 'description', 'domain', 'website', 'location',
        'email', 'phone', 'postal_address', 'bank_account',
    ],
    delegate: [
        'email', 'phone', 'postal_address', 'bank_account', 'date_of_birth',
        'display_name', 'professional_role', 'organization_name', 'website', 'location', 'biography',
    ],
    // registry_signing_key omitted: created automatically inside agent
    // registration in agents-list-pane (Phase 5 cleanup) — surfacing it as a
    // creatable claim type would let the user double-create one through the
    // generic claim flow, which the validator can't satisfy.
    agent: ['label', 'agent_type'],
};

function getClaimTypeOptions(): SelectOption<string>[] {
    const accountType = identity.getActive()?.accountType ?? 'natural_person';
    const types = CLAIM_TYPES_BY_IDENTITY[accountType] ?? CLAIM_TYPES_BY_IDENTITY['natural_person'];
    return types.map(t => ({ key: t, label: claimTypeLabel(t) }));
}

// ============================================================================
// Field definitions
// ============================================================================

const viewFields: ViewFieldDef<ClaimViewModel>[] = [
    { key: 'claimType', label: 'Type', type: 'text', format: (v) => claimTypeLabel(v as string) },
    { key: 'decryptedValue', label: 'Value', type: 'text' },
    { key: 'label', label: 'Label', type: 'text', format: (v) => (v as string) || '—', visible: (m) => supportsLabel(m.claimType) },
    {
        key: 'validationStatus', label: 'Validation', type: 'custom',
        create: (container) => ({
            render: (_value: unknown, model: ClaimViewModel) => {
                container.innerHTML = '';
                const status = deriveValidationStatus(model.validationStatus, model.certificates, model.revokedAt);
                const cert = model.certificates.find(c => c.isActive);
                container.appendChild(createCertificateBadge({
                    status, expiresAt: cert?.expiresAt, method: cert?.validationType,
                }));
            },
        }),
    },
    {
        key: 'publication', label: 'Publication', type: 'custom',
        // Validation (above) certifies the claim's content; publication is the
        // separate operator-review lifecycle that makes it publicly resolvable.
        // Shown when a publication row exists (covers registry_signing_key,
        // which publishes via the registry module's IPC rather than this
        // pane's action) or when the user could publish from here. Hidden for
        // org claims — their publicclaim rows belong to the org account and
        // are not visible through the session-scoped list.
        visible: (m) => !!m.claimUuid && !m.isOrgClaim && (
            m.publication !== null ||
            canPublish(m.claimType, identity.getActive()?.accountType ?? 'natural_person')
        ),
        create: (container) => ({
            render: (_value: unknown, model: ClaimViewModel) => {
                container.innerHTML = '';
                container.appendChild(createPublicationBadge({
                    status: derivePublicationStatus(model.publication),
                    rejectionReason: model.publication?.rejectionReason,
                }));
            },
        }),
    },
    {
        key: 'vouchingStatus', label: 'Vouching', type: 'custom',
        create: (container) => ({
            render: (_value: unknown, model: ClaimViewModel) => {
                container.innerHTML = '';
                if (!model.claimUuid || model.isOrgClaim) return;
                const statusEl = createElement('span', {
                    className: 'cid-wizard-status', textContent: 'Checking...',
                });
                container.appendChild(statusEl);
                void trustApi.getVouchingStatus(model.claimUuid).then(vs => {
                    container.innerHTML = '';
                    if (vs.required === 0) {
                        container.appendChild(createElement('span', {
                            textContent: 'No endorsements required',
                        }));
                    } else {
                        const metClass = vs.met ? 'cid-trust-vouch-met' : 'cid-trust-vouch-unmet';
                        container.appendChild(createElement('span', {
                            className: metClass,
                            textContent: `${vs.current}/${vs.required} endorsements (tier ${vs.minEndorserTier ?? 0}+)`,
                        }));
                        if (vs.met) {
                            container.appendChild(createElement('span', {
                                className: 'cid-trust-vouch-check',
                                textContent: ' Ready to publish',
                            }));
                        }
                    }
                }).catch(() => {
                    container.innerHTML = '';
                    container.appendChild(createElement('span', { textContent: '—' }));
                });
            },
        }),
    },
    { key: 'sharingCount', label: 'Shared With', type: 'number' },
    { key: 'creationTime', label: 'Created', type: 'date', format: 'datetime' },
    { key: 'claimUuid', label: 'Claim ID', type: 'text', visible: (m) => !!m.claimUuid },
    {
        key: 'certificates', label: 'Certificates', type: 'custom',
        visible: (m) => m.certificates.length > 0,
        create: (container) => ({
            render: (_value: unknown, model: ClaimViewModel) => {
                container.innerHTML = '';
                if (model.certificates.length === 0) return;

                const group = createFieldGroup({
                    title: `Certificates (${model.certificates.length})`,
                    collapsible: true,
                    collapsed: true,
                    labelSize: 'm',
                });

                for (const cert of model.certificates) {
                    // Parse the signed certificate JSON for extra details
                    let method = cert.validationType;
                    let confidence: number | null = null;
                    let validatorId: string | null = null;
                    try {
                        const parsed = JSON.parse(cert.certificateData);
                        const content = parsed.content ?? parsed;
                        method = content.validation?.method ?? content.validation?.type ?? cert.validationType;
                        confidence = content.validation?.confidence ?? null;
                        validatorId = content.validator_id ?? null;
                    } catch { /* use defaults */ }

                    const certExpired = cert.expiresAt ? new Date(cert.expiresAt) < new Date() : false;
                    const statusLabel = cert.isActive ? (certExpired ? 'Expired' : 'Active') : 'Inactive';
                    const statusColor = cert.isActive ? (certExpired ? '#ea4' : '#5c5') : '#888';

                    // Header row: type + status
                    const headerRow = createFormRow({ label: 'Type' });
                    headerRow.content.appendChild(createElement('span', {
                        textContent: `${method}`,
                    }));
                    headerRow.content.appendChild(createElement('span', {
                        textContent: ` — ${statusLabel}`,
                        style: { color: statusColor, marginLeft: '8px' },
                    }));
                    group.body.appendChild(headerRow.element);

                    // Issued
                    const issuedRow = createFormRow({ label: 'Issued' });
                    issuedRow.content.textContent = new Date(cert.issuedAt).toLocaleString();
                    group.body.appendChild(issuedRow.element);

                    // Expires
                    const expiresRow = createFormRow({ label: 'Expires' });
                    expiresRow.content.textContent = cert.expiresAt
                        ? new Date(cert.expiresAt).toLocaleString()
                        : 'Never';
                    group.body.appendChild(expiresRow.element);

                    // Confidence
                    if (confidence !== null) {
                        const confRow = createFormRow({ label: 'Confidence' });
                        confRow.content.textContent = `${confidence}%`;
                        group.body.appendChild(confRow.element);
                    }

                    // Validator
                    if (validatorId) {
                        const valRow = createFormRow({ label: 'Validator' });
                        valRow.content.textContent = validatorId;
                        valRow.content.style.fontSize = '11px';
                        valRow.content.style.opacity = '0.7';
                        group.body.appendChild(valRow.element);
                    }

                    // Separator between certificates (if more than one)
                    if (model.certificates.indexOf(cert) < model.certificates.length - 1) {
                        group.body.appendChild(createElement('hr', {
                            style: { border: 'none', borderTop: '1px solid rgba(255,255,255,0.1)', margin: '8px 0' },
                        }));
                    }
                }

                container.appendChild(group.element);
            },
        }),
    },
];

const editFields: FormFieldDef<ClaimViewModel>[] = [
    { key: 'decryptedValue', label: 'Value', type: 'text', multiline: true, rows: 3 },
    { key: 'label', label: 'Label', type: 'text', placeholder: 'Optional label', visible: (m) => supportsLabel(m.claimType) },
];

const newFields: FormFieldDef<ClaimViewModel>[] = [
    { key: 'claimType', label: 'Type', type: 'select', options: [], required: true },
    { key: 'decryptedValue', label: 'Value', type: 'text', multiline: true, rows: 3, required: true },
    { key: 'label', label: 'Label', type: 'text', placeholder: 'Optional label', visible: (m) => supportsLabel(m.claimType) },
];

/** Claim types where a label makes sense (multiple instances are common). */
const LABEL_TYPES = new Set([
    'email', 'phone', 'website', 'postal_address', 'bank_account',
]);

function supportsLabel(claimType: string): boolean {
    return LABEL_TYPES.has(claimType);
}

// ============================================================================
// Publication eligibility (mirrors server ClaimTypes.cs §4.5.4)
// ============================================================================

const PUBLISHABLE_BY: Record<string, string[]> = {
    email: ['natural_person', 'organization', 'delegate'],
    phone: ['natural_person', 'organization', 'delegate'],
    postal_address: ['organization'],
    domain: ['organization'],
    // registry_signing_key omitted: published via the standalone registry
    // module's cross-module IPC (Phase 5 Step D) for personal/delegate
    // accounts. Agent flow creates the claim but does not publish it.
    display_name: ['natural_person', 'organization', 'delegate'],
    professional_role: ['natural_person', 'delegate'],
    organization: ['natural_person'],
    website: ['natural_person', 'organization', 'delegate'],
    location: ['natural_person', 'organization', 'delegate'],
    biography: ['natural_person', 'delegate'],
    description: ['organization'],
    organization_name: ['delegate'],
    label: ['agent'],
    agent_type: ['agent'],
};

function canPublish(claimType: string, accountType: string): boolean {
    const allowed = PUBLISHABLE_BY[claimType];
    return allowed ? allowed.includes(accountType) : false;
}

// ============================================================================
// Actions
// ============================================================================

const actions: EntityAction<ClaimViewModel>[] = [
    {
        key: 'share',
        label: 'Share',
        modes: ['view'],
        overflow: true,
        execute: (_model, ctx) => {
            const surface = ctx.shell.getSurfaceForPane(ctx.instance.instanceId);
            surface?.openPane({
                typeId: PANE_IDS.sharing,
                title: 'Sharing',
                context: { claimUuid: ctx.instance.context['claimUuid'] },
            }, { originPaneId: ctx.instance.instanceId });
        },
    },
    {
        key: 'validate',
        label: 'Validate',
        modes: ['view'],
        canExecute: (model) => {
            if (model.validationStatus === 'pending' || model.revokedAt) return false;
            // Disable if validated with a non-expired certificate (revalidation only needed after expiry)
            if (model.validationStatus === 'validated') {
                const hasActiveCert = model.certificates.some(
                    c => c.isActive !== false && (!c.expiresAt || new Date(c.expiresAt) > new Date()));
                if (hasActiveCert) return false;
            }
            return true;
        },
        disabledReason: (model) => {
            if (model.revokedAt) return 'This claim has been revoked';
            if (model.validationStatus === 'pending') return 'A validation is already pending';
            return 'Already validated — revalidate once the certificate expires';
        },
        execute: async (model, ctx) => {
            // registry_signing_key validation now happens entirely inside the
            // standalone registry module's publish flow (Phase 5 Step C4 — the
            // cld-id browser orchestrates create → validate → sign-challenge
            // → verify in one round-trip with the registry vault). Cld-id's
            // claim-detail UI no longer offers a manual validate path for
            // registry_signing_key claims; agent-created claims may surface here
            // already certified by agents-list-pane's inline ownership proof.
            const typeMap: Record<string, string> = {
                email: 'email', phone: 'phone', postal_address: 'postal_address',
                bank_account: 'bank_account', domain: 'domain',
            };
            const validationType = typeMap[model.claimType];
            if (!validationType) {
                ctx.shell.toast(`Claim type "${model.claimType}" cannot be validated`, 'warning');
                return;
            }
            const selfKey = model.claimKeys.find(k => k.recipientType === 'self');
            if (!selfKey) { ctx.shell.toast('No encryption key found', 'error'); return; }

            // Re-encrypt the claim's symmetric key for the validator (§4.3, CR3-C1)
            const attrSecretKeyB64 = keys.getPrivateKey('attr_encrypt');
            if (!attrSecretKeyB64) { ctx.shell.toast('Decryption key not available on this device', 'error'); return; }
            let validatorEncryptedKey: string;
            try {
                const { publicKey: validatorKeyB64 } = await claimsApi.getValidatorKey();
                const validatorPublicKey = aes.fromBase64(validatorKeyB64);
                if (selfKey.keyIndex > 0) {
                    // Post-rotation: chain-walk to unwrap, then re-wrap for validator
                    const keyChain = keys.getKeyChain('attr_encrypt');
                    validatorEncryptedKey = await reEncryptClaimKeyWithChain(
                        selfKey.encryptedKey, keyChain, selfKey.keyIndex, validatorPublicKey);
                } else {
                    validatorEncryptedKey = await reEncryptClaimKey(
                        selfKey.encryptedKey, aes.fromBase64(attrSecretKeyB64), validatorPublicKey);
                }
            } catch (e: any) {
                ctx.shell.toast(`Failed to prepare key for validator: ${e.message}`, 'error');
                return;
            }

            try {
                const result = await claimsApi.validateClaim(model.claimUuid, { validationType, encryptedKey: validatorEncryptedKey });

                // Domain validation: show modal with token + instructions + verify button
                if (validationType === 'domain' && result.verificationToken) {
                    showDomainVerificationModal(ctx, model.claimUuid, result.verificationToken);
                    return;
                }

                // Phone validation: prompt for the SMS code and verify it.
                if (validationType === 'phone' && result.verificationToken) {
                    showPhoneVerificationModal(ctx, result.verificationToken);
                    return;
                }

                const msgs: Record<string, string> = {
                    email: 'Verification email sent. Check your inbox.',
                    phone: 'Verification code sent by SMS.',
                };
                ctx.shell.toast(msgs[validationType] ?? 'Validation requested', 'success');
                ctx.entityView.reload();
            } catch (err) {
                ctx.shell.toast(err instanceof Error ? err.message : 'Validation failed', 'error');
            }
        },
    },
    // registry_signing_key submissions / revoke / unpublish actions removed in
    // Phase 5 Step F: the standalone registry module is now the source of truth
    // for per-machine signing keys (publish / unpublish / republish flow lives
    // on the registry key-detail pane; revoke propagates back to cld-id via
    // cross-module IPC, see Step D's revoke endpoint extension).
    {
        key: 'publish',
        label: 'Publish',
        modes: ['view'],
        canExecute: (model) => {
            if (model.revokedAt) return false;
            const accountType = identity.getActive()?.accountType ?? 'natural_person';
            return canPublish(model.claimType, accountType);
        },
        disabledReason: (model) => model.revokedAt
            ? 'This claim has been revoked'
            : `${claimTypeLabel(model.claimType)} claims cannot be published from this account`,
        execute: async (model, ctx) => {
            const accountType = identity.getActive()?.accountType ?? 'natural_person';
            if (!canPublish(model.claimType, accountType)) {
                ctx.shell.toast(`"${claimTypeLabel(model.claimType)}" cannot be published by ${accountType} accounts`, 'error');
                return;
            }

            const selfKey = model.claimKeys.find(k => k.recipientType === 'self');
            if (!selfKey) { ctx.shell.toast('No encryption key found', 'error'); return; }
            const mySecretKey = keys.getPrivateKey('attr_encrypt');
            if (!mySecretKey) { ctx.shell.toast('Private key not available — unlock keys first', 'error'); return; }

            try {
                // Check if this claim already has an existing publication
                const existingPublic = await publicClaimsApi.listPublicClaims();
                const existingForClaim = existingPublic.find(
                    pc => pc.claimUuid === model.claimUuid,
                );
                if (existingForClaim?.status === 'published') {
                    const confirmed = await ctx.shell.confirm({
                        title: 'Re-publish Claim',
                        message: 'This claim is already published. Re-publishing will remove the current publication and submit a new one for operator review.',
                        confirmLabel: 'Re-publish',
                        confirmVariant: 'no',
                    });
                    if (!confirmed) return;
                } else if (existingForClaim?.status === 'pending') {
                    const confirmed = await ctx.shell.confirm({
                        title: 'Replace Pending Request',
                        message: 'This claim already has a pending publication request. Submitting again will replace it.',
                        confirmLabel: 'Replace',
                        confirmVariant: 'no',
                    });
                    if (!confirmed) return;
                }

                // Fetch operators and share the claim key with each
                const operators = await publicClaimsApi.listOperators();
                if (operators.length === 0) {
                    ctx.shell.toast('No operators available to review publications. Contact your node administrator.', 'error');
                    return;
                }

                const operatorKeys = [];
                for (const op of operators) {
                    let reEncrypted: string;
                    if (selfKey.keyIndex > 0) {
                        const keyChain = keys.getKeyChain('attr_encrypt');
                        reEncrypted = await reEncryptClaimKeyWithChain(
                            selfKey.encryptedKey, keyChain, selfKey.keyIndex, aes.fromBase64(op.publicKey));
                    } else {
                        reEncrypted = await reEncryptClaimKey(
                            selfKey.encryptedKey, aes.fromBase64(mySecretKey), aes.fromBase64(op.publicKey));
                    }
                    operatorKeys.push({ operatorCid: op.cid, encryptedKey: reEncrypted });
                }

                await publicClaimsApi.publishClaim(model.claimUuid, { operatorKeys });

                // Auto-approve if the publishing session is itself an operator —
                // an owner/admin delegate of a bound org, or that org's account
                // session (operator-org-binding plan §2.7). Self-review is
                // redundant for them; every other session takes the else branch
                // and waits for review. operatorSignature is computed
                // server-side with the node key; the client just sends the value.
                if (session.isOperator()) {
                    const publicValue = model.decryptedValue;
                    const allPublic = await publicClaimsApi.listPublicClaims();
                    const pending = allPublic.find(pc => pc.claimUuid === model.claimUuid && pc.status === 'pending');
                    if (pending) {
                        await publicClaimsApi.reviewPublicClaim(pending.publicClaimId, {
                            approved: true,
                            publicValue,
                        });
                    }
                    ctx.shell.emit('data-changed', { type: 'publicclaims' });
                    ctx.shell.toast('Claim published.', 'success');
                } else {
                    ctx.shell.emit('data-changed', { type: 'publicclaims' });
                    ctx.shell.toast('Publication request submitted. An operator will review your claim.', 'success');
                }
                ctx.entityView.reload();
            } catch (err) {
                ctx.shell.toast(err instanceof Error ? err.message : 'Publication request failed', 'error');
            }
        },
    },
    {
        key: 'cancel-validation',
        label: 'Cancel Validation',
        modes: ['view'],
        overflow: true,
        variant: 'danger',
        canExecute: (model) => model.validationStatus === 'pending',
        disabledReason: () => 'Only a pending validation can be cancelled',
        execute: async (model, ctx) => {
            const ok = await ctx.shell.confirm({
                title: 'Cancel Validation',
                message: 'Cancel the pending validation? You can re-validate later.',
                confirmLabel: 'Cancel Validation',
                confirmVariant: 'no',
            });
            if (!ok) return;
            try {
                await claimsApi.cancelValidation(model.claimUuid);
                ctx.shell.toast('Validation cancelled', 'success');
                ctx.entityView.reload();
            } catch (err) {
                ctx.shell.toast(err instanceof Error ? err.message : 'Failed to cancel', 'error');
            }
        },
    },
];

// ============================================================================
// Domain Verification Modal
// ============================================================================

function showDomainVerificationModal(
    ctx: { shell: ShellAPI; entityView: { reload: () => void } },
    claimUuid: string,
    token: string,
): void {
    const content = createElement('div', { className: 'cid-wizard-step' });
    content.appendChild(createElement('h3', {
        className: 'cid-wizard-heading',
        textContent: 'Domain Verification',
    }));

    content.appendChild(createElement('p', {
        className: 'cid-wizard-text',
        textContent: 'Place the following token in a text file at the path shown below on your domain, then click "Verify".',
    }));

    // Token display
    const tokenGroup = createFieldGroup({ title: 'Verification Token' });
    const tokenRow = createElement('div', { className: 'cid-key-field' });
    const tokenValue = createElement('code', {
        className: 'cid-key-field-value',
        textContent: token,
    });
    tokenValue.style.wordBreak = 'break-all';
    tokenValue.style.userSelect = 'all';
    tokenRow.appendChild(tokenValue);
    tokenRow.appendChild(createCopyButton({ getText: () => token }));
    tokenGroup.body.appendChild(tokenRow);
    content.appendChild(tokenGroup.element);

    // Decode domain from claim (we don't have it here, so instruct generically)
    content.appendChild(createElement('p', {
        className: 'cid-wizard-text',
        textContent: 'File path: https://yourdomain.com/.well-known/catalogid.txt',
    }));
    content.appendChild(createElement('p', {
        className: 'cid-pane-note',
        textContent: 'The file must contain only the token above, with no extra whitespace or HTML.',
    }));

    const statusEl = createElement('div', { className: 'cid-trust-success' });
    const errorEl = createElement('div', { className: 'cid-pane-error' });
    content.appendChild(errorEl);
    content.appendChild(statusEl);

    // We need the domain value to verify. Decrypt the claim to get it.
    const btnRow = createElement('div', { className: 'cid-dashboard-actions' });
    const verifyBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes',
        textContent: 'Verify',
    });
    verifyBtn.addEventListener('click', async () => {
        verifyBtn.setAttribute('disabled', '');
        errorEl.classList.remove('cid-pane-error--visible');
        statusEl.textContent = 'Verifying...';

        try {
            // Decrypt claim to get domain value
            const attrKey = keys.getPrivateKey('attr_encrypt');
            if (!attrKey) throw new Error('Decryption key not available');
            const detail = await claimsApi.getClaim(claimUuid);
            const selfKey = detail.keys.find(k => k.recipientType === 'self');
            if (!selfKey) throw new Error('No self key for claim');
            const domain = await decryptClaim(detail.encryptedValue, detail.nonce, selfKey.encryptedKey, aes.fromBase64(attrKey));

            const result = await claimsApi.verifyDomain(token, domain);
            if (result.success) {
                statusEl.textContent = 'Domain verified successfully!';
                ctx.entityView.reload();
                setTimeout(() => modal.close(), 1500);
            } else {
                statusEl.textContent = '';
                errorEl.textContent = 'Verification failed — token not found at .well-known/catalogid.txt';
                errorEl.classList.add('cid-pane-error--visible');
                verifyBtn.removeAttribute('disabled');
            }
        } catch (e) {
            statusEl.textContent = '';
            errorEl.textContent = e instanceof Error ? e.message : 'Verification failed';
            errorEl.classList.add('cid-pane-error--visible');
            verifyBtn.removeAttribute('disabled');
        }
    });
    btnRow.appendChild(verifyBtn);
    content.appendChild(btnRow);

    const modal = ctx.shell.showModal(content);
}

// ============================================================================
// Phone Verification Modal
// ============================================================================

function showPhoneVerificationModal(
    ctx: { shell: ShellAPI; entityView: { reload: () => void } },
    token: string,
): void {
    const content = createElement('div', { className: 'cid-wizard-step' });
    content.appendChild(createElement('h3', {
        className: 'cid-wizard-heading',
        textContent: 'Phone Verification',
    }));
    content.appendChild(createElement('p', {
        className: 'cid-wizard-text',
        textContent: 'Enter the verification code we just sent by SMS.',
    }));

    const input = createElement('input', { className: 'cid-auth-input' }) as HTMLInputElement;
    input.type = 'text';
    input.inputMode = 'numeric';
    input.autocomplete = 'one-time-code';
    input.maxLength = 10;
    input.placeholder = 'Verification code';
    content.appendChild(input);

    const statusEl = createElement('div', { className: 'cid-trust-success' });
    const errorEl = createElement('div', { className: 'cid-pane-error' });
    content.appendChild(errorEl);
    content.appendChild(statusEl);

    const btnRow = createElement('div', { className: 'cid-dashboard-actions' });
    const verifyBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes',
        textContent: 'Verify',
    });

    const submit = async () => {
        const code = input.value.trim();
        if (!code) {
            errorEl.textContent = 'Enter the code from the SMS.';
            errorEl.classList.add('cid-pane-error--visible');
            return;
        }
        verifyBtn.setAttribute('disabled', '');
        errorEl.classList.remove('cid-pane-error--visible');
        statusEl.textContent = 'Verifying...';

        try {
            const result = await claimsApi.verifyPhone(token, code);
            if (result.success) {
                statusEl.textContent = 'Phone number verified successfully!';
                ctx.entityView.reload();
                setTimeout(() => modal.close(), 1500);
            } else {
                // The code is checked by the provider; a wrong/expired code fails here.
                statusEl.textContent = '';
                errorEl.textContent = 'Incorrect or expired code. Please try again.';
                errorEl.classList.add('cid-pane-error--visible');
                verifyBtn.removeAttribute('disabled');
            }
        } catch (e) {
            statusEl.textContent = '';
            errorEl.textContent = e instanceof Error ? e.message : 'Verification failed';
            errorEl.classList.add('cid-pane-error--visible');
            verifyBtn.removeAttribute('disabled');
        }
    };

    verifyBtn.addEventListener('click', () => void submit());
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter') void submit(); });
    btnRow.appendChild(verifyBtn);
    content.appendChild(btnRow);

    const modal = ctx.shell.showModal(content);
    input.focus();
}

// ============================================================================
// Config
// ============================================================================

const config: EntityPaneConfig<ClaimViewModel> = {
    typeId: PANE_IDS.claimDetail,
    defaultTitle: 'Claim',
    ariaLabel: 'Claim details',
    // 'publicclaims' too: an operator approving/rejecting the publication
    // only touches the publicclaim row, and the Publication field must follow.
    refreshOnDataChanged: ['claims', 'publicclaims'],
    // Verbs live in the pane's own title row (shell USAGE.md "Where a button
    // belongs"): Validate / Publish (and the factory's Edit + Delete) inline,
    // Share and Cancel Validation under the trailing ⋯ menu.
    actionsPlacement: 'pane',

    getModel: async (ctx) => {
        const uuid = ctx.instance.context['claimUuid'] as string | undefined;
        const isOrgClaim = ctx.instance.context['isOrgClaim'] === true;
        if (uuid) return loadModel(uuid, isOrgClaim);
        return newClaimModel(ctx.instance.context);
    },

    initialMode: (ctx) => ctx.instance.context['mode'] === 'new' ? 'new' : 'view',

    view: {
        fields: viewFields,
        // Two columns of short fields; only the (possibly long) value and the
        // certificate list take a full row. A lone short field in a row of its
        // own reads as a hole beside it (2026-09-04).
        layout: [
            ['claimType', 'label'],
            ['decryptedValue'],
            ['validationStatus', 'publication'],
            ['sharingCount', 'vouchingStatus'],
            ['creationTime', 'claimUuid'],
            ['certificates'],
        ],
    },

    form: {
        fields: editFields,
        layout: [['decryptedValue'], ['label']],
    },

    newForm: () => ({
        fields: [
            { ...newFields[0], options: getClaimTypeOptions() } as FormFieldDef<ClaimViewModel>,
            newFields[1],
            newFields[2],
        ],
        layout: [['claimType'], ['decryptedValue'], ['label']],
    }),

    canEdit: (model) => !model.revokedAt,
    canDelete: (model) => !model.revokedAt,

    onUpdate: async (model, ctx) => {
        const attrKey = keys.getPrivateKey('attr_encrypt');
        if (!attrKey) throw new Error('Private key not available');

        const selfKey = model.claimKeys.find(k => k.recipientType === 'self');
        if (!selfKey) throw new Error('Self claim key not found');

        const encrypted = await reEncryptClaimValue(
            model.decryptedValue, selfKey.encryptedKey, aes.fromBase64(attrKey),
        );
        await claimsApi.updateClaim(model.claimUuid, encrypted);
        ctx.shell.emit('data-changed', { type: 'claims' });
        ctx.shell.toast('Claim updated. Previous validation has been invalidated.', 'warning');
    },

    onCreate: async (model, ctx) => {
        // Personal claim: our own key, /api/claims. Org claim: the ORG's key,
        // the org's claims route — see claimCreateTarget.
        const target = claimCreateTarget(model.isOrgClaim, identity.getOrganizationCid());
        const pubKeys = target.orgCid
            ? await accountApi.getPublicKeys(target.orgCid)
            : await accountApi.getOwnPublicKeys();
        // After a key rotation an account can publish more than one active
        // attr_encrypt generation; the newest is the one the current secret key
        // matches, so wrap to that one (same rule as the flatten path).
        const attrPub = pubKeys
            .filter(k => k.keyId === 'attr_encrypt')
            .sort((a, b) => b.keyIndex - a.keyIndex)[0];
        if (!attrPub) throw new Error('No attr_encrypt public key found');

        const encrypted = await encryptClaim(
            model.decryptedValue, aes.fromBase64(attrPub.publicKey), model.label || undefined);
        const request = {
            claimType: model.claimType,
            encryptedValue: encrypted.encryptedValue,
            nonce: encrypted.nonce,
            encryptedKey: encrypted.encryptedKey,
            encryptedLabel: encrypted.encryptedLabel,
            labelNonce: encrypted.labelNonce,
        };
        const resp = target.orgCid
            ? await organizationApi.createOrgClaim(target.orgCid, request)
            : await claimsApi.createClaim(request);

        // Store the UUID so getModel() can load the full claim after save
        ctx.instance.context['claimUuid'] = resp.claimUuid;
        ctx.surface?.updatePaneTitle(ctx.instance.instanceId, claimTypeLabel(model.claimType));

        // Auto-validate email claims (mirrors registration wizard behavior).
        // ORG claims are excluded (target.autoValidateEmail === false): the
        // re-encryption below opens the claim key with the CREATOR's own
        // attr_encrypt secret key, and an org claim's key is wrapped to the
        // ORGANIZATION's public key — the wrong lock. Validating an org claim
        // needs the unwrapped org key and is out of scope; the delegate can
        // still start it later from the claim's Validate button.
        if (target.autoValidateEmail && model.claimType === 'email') {
            try {
                const attrSecretKeyB64 = keys.getPrivateKey('attr_encrypt');
                if (attrSecretKeyB64) {
                    const { publicKey: validatorKeyB64 } = await claimsApi.getValidatorKey();
                    const validatorEncryptedKey = await reEncryptClaimKey(
                        encrypted.encryptedKey, aes.fromBase64(attrSecretKeyB64), aes.fromBase64(validatorKeyB64),
                    );
                    await claimsApi.validateClaim(resp.claimUuid, {
                        validationType: 'email', encryptedKey: validatorEncryptedKey,
                    });
                    ctx.shell.emit('data-changed', { type: 'claims' });
                    ctx.shell.toast('Claim created. Verification email sent — check your inbox.', 'success');
                    return;
                }
            } catch {
                // Validation failed but claim was created — show partial success
                ctx.shell.emit('data-changed', { type: 'claims' });
                ctx.shell.toast('Claim created, but automatic validation failed. Use the Validate button.', 'warning');
                return;
            }
        }

        ctx.shell.emit('data-changed', { type: 'claims' });
        ctx.shell.toast('Claim created and encrypted', 'success');
    },

    onDelete: async (ctx) => {
        await claimsApi.deleteClaim(ctx.instance.context['claimUuid'] as string);
        ctx.shell.emit('data-changed', { type: 'claims' });
        ctx.shell.toast('Claim deleted', 'success');
    },

    deleteConfirmMessage: 'This will permanently delete this claim and all associated sharing relationships. This cannot be undone.',
    actions,
};

export function createClaimDetailPane() {
    return createEntityPaneType(config);
}
